import React, { useState } from 'react';
import { VersionBadge, VersionDisplay } from '../../../components/VersionDisplay';
import { PanelContainer } from '../components/core/PanelContainer';
import { TwitchEmbed } from '../components/complex/TwitchEmbed';
import { Button } from '../primitives/Button';
import { COLORS, SPACE, FONT_SIZE, FONT_WEIGHT, RADII } from '../theme';

interface LandingShellProps {
  onStartGame: () => void;
  onOpenCharacterCreator: () => void;
  onOpenNPCCreator: () => void;
  onOpenGallery: () => void;
}

export const LandingShell: React.FC<LandingShellProps> = ({
  onStartGame,
  onOpenCharacterCreator,
  onOpenNPCCreator,
  onOpenGallery
}) => {
  const [activeModal, setActiveModal] = useState<'about' | 'credits' | 'twitch' | null>(null);

  const renderModal = () => {
    if (!activeModal) return null;

    const overlayStyle: React.CSSProperties = {
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: COLORS.overlayScrimStrong,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    };

    let content = null;
    if (activeModal === 'about') {
      content = (
        <PanelContainer title="About Helix 2000" onClose={() => setActiveModal(null)} width="500px">
          <p style={{ color: COLORS.textSecondary, lineHeight: 1.6, marginBottom: SPACE.md }}>
            Welcome to the Fantasy Realm of Helix 2000 — a live multiplayer browser MMO.
            Pick a class, drop into the lobby, and play with whoever else is online.
          </p>
          <p style={{ color: COLORS.textSecondary, lineHeight: 1.6 }}>
            Currently in Multiplayer Alpha — 5 classes, 500 abilities, server-authoritative
            movement. Help us shape the future of the game!
          </p>
        </PanelContainer>
      );
    } else if (activeModal === 'credits') {
      content = (
        <PanelContainer title="Credits" onClose={() => setActiveModal(null)} width="400px">
          <ul style={{ color: COLORS.textSecondary, lineHeight: 1.8, listStyle: 'none', padding: 0 }}>
            <li><strong>Lead Developer:</strong> djmsqrvve</li>
            <li><strong>Engine:</strong> Phaser 3 & React</li>
            <li><strong>Art & Assets:</strong> Community & Open Source</li>
          </ul>
        </PanelContainer>
      );
    } else if (activeModal === 'twitch') {
      content = (
        <PanelContainer title="Live Development" onClose={() => setActiveModal(null)} width="800px" height="500px">
          <TwitchEmbed channel="djmsqrvve" />
        </PanelContainer>
      );
    }

    return (
      <div style={overlayStyle} onClick={(e) => { if(e.target === e.currentTarget) setActiveModal(null); }}>
        {content}
      </div>
    );
  };

  return (
    <div className="fantasy-background">
      <div className="particles">
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
      </div>

      <div className="splash-container" style={{ position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', height: '100%' }}>

        <div className="logo-section" style={{ marginTop: 'auto' }}>
          <div className="game-title">
            <h1 className="title-main" style={{ fontSize: '4rem' }}>⚔️ HELIX 2000 ⚔️</h1>
            <h2 className="title-sub" style={{ color: COLORS.brandAccent }}>Alpha <VersionBadge /></h2>
            <h3 style={{ color: COLORS.textSecondary, fontWeight: 300, marginTop: SPACE.sm }}>Multiplayer Alpha</h3>
          </div>
          <div className="floating-orb"></div>
        </div>

        <div className="content-section" style={{ marginBottom: 'auto' }}>
          <div className="action-buttons" style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md, maxWidth: '400px', margin: '0 auto' }}>
            <Button onClick={onStartGame} variant="primary" fullWidth style={{ padding: SPACE.xxl, fontSize: FONT_SIZE.title }}>
              🎮 Enter Game World
            </Button>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE.md }}>
              <Button onClick={onOpenCharacterCreator} variant="secondary">
                ✨ Characters
              </Button>
              <Button onClick={onOpenNPCCreator} variant="secondary">
                🐷 NPCs
              </Button>
            </div>

            <Button onClick={() => window.location.href = '/diagnostic'} variant="danger" fullWidth>
              🔧 Network Diagnostic
            </Button>

            <Button onClick={onOpenGallery} variant="secondary" fullWidth>
              🎨 View Concept Art
            </Button>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: SPACE.xxl, padding: SPACE.lg }}>
          <div style={{ display: 'flex', gap: SPACE.md }}>
            <Button variant="ghost" size="xs" onClick={() => setActiveModal('about')}>
              About
            </Button>
            <Button variant="ghost" size="xs" onClick={() => setActiveModal('credits')}>
              Credits
            </Button>
          </div>
          <Button
            onClick={() => setActiveModal('twitch')}
            style={{
              background: 'rgba(145, 70, 255, 0.2)',
              border: '1px solid rgba(145, 70, 255, 0.5)',
              color: COLORS.textPrimary,
              padding: `${SPACE.sm}px ${SPACE.lg}px`,
              borderRadius: RADII.pill,
              boxShadow: 'none',
              fontWeight: FONT_WEIGHT.semibold,
            }}
          >
            <span
              aria-hidden
              style={{ width: '10px', height: '10px', background: '#ff0000', borderRadius: '50%', boxShadow: '0 0 8px #ff0000' }}
            />
            Watch Live
          </Button>
        </div>
      </div>

      {renderModal()}
      <VersionDisplay position="top-right" />
    </div>
  );
};
